import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { getCatalog } from "../i18n";
import { useMotionPolicy } from "../lib/hooks/useMotionPolicy";
import { motionEase } from "../lib/motion";
import { useNexusStore } from "../store/nexusStore";

const reveal = {
  hidden: { y: "108%", opacity: 0 },
  visible: (delay: number) => ({
    y: "0%",
    opacity: 1,
    transition: { duration: 0.8, delay, ease: motionEase },
  }),
};

const introSessionKey = "kx:intro-seen";

function shouldShowIntro() {
  try {
    if (window.sessionStorage.getItem(introSessionKey)) return false;
    window.sessionStorage.setItem(introSessionKey, "1");
  } catch {
    // A restricted storage context should not prevent the experience from loading.
  }
  return true;
}

export function IntroVeil() {
  const [visible, setVisible] = useState(shouldShowIntro);
  const locale = useNexusStore((state) => state.locale);
  const motionPolicy = useMotionPolicy();
  const ui = getCatalog(locale).ui;

  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(false), motionPolicy.reduced ? 100 : 2600);
    return () => window.clearTimeout(timer);
  }, [motionPolicy.reduced]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="intro-veil"
          role="status"
          aria-label={`${ui.introSub}. ${ui.introTitle}`}
          exit={motionPolicy.reduced ? { opacity: 0 } : { clipPath: "inset(0 0 100% 0)" }}
          transition={{ duration: motionPolicy.reduced ? 0 : 0.82, ease: motionEase }}
        >
          <div className="intro-veil__frame" aria-hidden="true">
            <motion.span
              className="intro-frame__line"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, ease: motionEase }}
            />
            <motion.span
              className="intro-frame__line"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 1.2, ease: motionEase }}
            />
            <motion.span
              className="intro-frame__line"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, ease: motionEase }}
            />
            <motion.span
              className="intro-frame__line"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 1.2, ease: motionEase }}
            />
          </div>

          <div className="intro-veil__indices" aria-hidden="true">
            <span className="intro-index intro-index--origin">KX / 00—01</span>
            <span className="intro-index intro-index--coordinates intro-index--right">50°03′N / 19°56′E</span>
            <span className="intro-index intro-index--system intro-index--bottom">LIVE SYSTEM</span>
            <span className="intro-index intro-index--year intro-index--right intro-index--bottom">MMXXVI</span>
          </div>

          <div className="intro-orbit" aria-hidden="true">
            <motion.i
              className="intro-orbit__outer"
              initial={motionPolicy.reduced ? false : { opacity: 0, scale: 0.72, rotate: -24 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.45, ease: motionEase }}
            />
            <motion.i
              className="intro-orbit__inner"
              initial={motionPolicy.reduced ? false : { opacity: 0, scale: 1.28, rotate: 22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.45, delay: 0.08, ease: motionEase }}
            />
            <motion.span
              className="intro-orbit__core"
              initial={motionPolicy.reduced ? false : { scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.7, 1], opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: motionEase }}
            />
          </div>

          <div className="intro-copy">
            <div className="intro-copy__eyebrow">
              <motion.span
                className="intro-copy__line"
                custom={0.18}
                variants={reveal}
                initial={motionPolicy.reduced ? false : "hidden"}
                animate="visible"
              >
                {ui.introSub}
              </motion.span>
            </div>
            <div className="intro-wordmark" aria-hidden="true">
              <span className="intro-wordmark__segment">
                <motion.b
                  custom={0.28}
                  variants={reveal}
                  initial={motionPolicy.reduced ? false : "hidden"}
                  animate="visible"
                >
                  K
                </motion.b>
              </span>
              <motion.i
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.76, duration: 0.5, ease: motionEase }}
              />
              <span className="intro-wordmark__segment">
                <motion.b
                  custom={0.4}
                  variants={reveal}
                  initial={motionPolicy.reduced ? false : "hidden"}
                  animate="visible"
                >
                  NEXUS
                </motion.b>
              </span>
            </div>
            <div className="intro-copy__title">
              <motion.p
                className="intro-copy__line"
                custom={0.62}
                variants={reveal}
                initial={motionPolicy.reduced ? false : "hidden"}
                animate="visible"
              >
                {ui.introTitle}
              </motion.p>
            </div>
          </div>

          <div className="intro-calibration" aria-hidden="true">
            <span>SIGNAL ACQUIRED</span>
            <div>
              <motion.i
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 2.1, delay: 0.22, ease: "linear" }}
              />
            </div>
            <span>100.00</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
